import CloseIcon from "@mui/icons-material/Close";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { Menu, MenuItem, Tooltip, useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GridColDef } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { TableDensity } from "./DynamicDataTable";
import { menuItemSx, menuPaperSx } from "../lib/menu-chrome";
import { ColumnVisibilityManager } from "./ColumnVisibilityManager";
import { ExpansionTreeView } from "./ExpansionTreeView";
import { Button } from "./ui/Button";

interface DataTableToolbarProps {
  columns: GridColDef[];
  columnVisibility: { [key: string]: boolean };
  onColumnVisibilityChange: (newModel: { [key: string]: boolean }) => void;

  expandableFields: Map<
    string,
    { relationName: string; path: string; level: number; parentPath?: string }
  >;
  expandedPaths: string[];
  onToggleExpansion: (path: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;

  search?: string;
  onSearchChange?: (value: string) => void;
  searchableCount?: number;
  searchPlaceholder?: string;

  density?: TableDensity;
  onDensityChange?: (density: TableDensity) => void;

  onRefresh?: () => void;
  refreshing?: boolean;

  onExport?: () => void;
  /** Tooltip and aria-label for the export control. */
  exportLabel?: string;

  onImport?: (file: File) => void;
  /** Tooltip and aria-label for the import control. */
  importLabel?: string;

  /** Filter chips row, rendered underneath the controls. */
  filters?: React.ReactNode;
}

/** Shared chrome for the small bordered controls in the table furniture. */

/**
 * The one control strip above the grid: search, relation expansion, column
 * visibility, density, refresh, import and export. Filters render underneath so the
 * page no longer stacks three separate bars before the first row of data.
 */
export const DataTableToolbar: React.FC<DataTableToolbarProps> = React.memo(
  ({
    columns,
    columnVisibility,
    onColumnVisibilityChange,
    expandableFields,
    expandedPaths,
    onToggleExpansion,
    onExpandAll,
    onCollapseAll,
    search = "",
    onSearchChange,
    searchableCount = 0,
    searchPlaceholder,
    density = "comfortable",
    onDensityChange,
    onRefresh,
    refreshing,
    onExport,
    exportLabel,
    onImport,
    importLabel,
    filters,
  }) => {
    const [draft, setDraft] = useState(search);
    const [densityAnchor, setDensityAnchor] = useState<null | HTMLElement>(null);
    const [overflowAnchor, setOverflowAnchor] = useState<null | HTMLElement>(null);
    const [mobileMenu, setMobileMenu] = useState<
      "main" | "columns" | "density"
    >("main");
    const inputRef = useRef<HTMLInputElement>(null);
    const importInputRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();
    // defaultMatches false: first paint on a phone must not be the wrapping
    // desktop chip strip (useMediaQuery is false until the listener fires).
    const desktopTools = useMediaQuery(theme.breakpoints.up("sm"), {
      defaultMatches: false,
    });

    useEffect(() => setDraft(search), [search]);

    // Debounce so a five-character search does not fire five queries.
    useEffect(() => {
      if (!onSearchChange || draft === search) return;
      const timer = window.setTimeout(() => onSearchChange(draft), 350);
      return () => window.clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft]);

    const hasExpandable = expandableFields.size > 0;
    const canSearch = !!onSearchChange && searchableCount > 0;
    const closeOverflow = useCallback(() => {
      setOverflowAnchor(null);
      setMobileMenu("main");
    }, []);

    useEffect(() => {
      if (desktopTools) closeOverflow();
    }, [desktopTools, closeOverflow]);

    return (
      <div className="flex flex-col gap-2 border-b border-od-border bg-od-surface px-2.5 py-2">
        <input
          ref={importInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) onImport?.(file);
          }}
        />

        <div className="flex flex-wrap items-center gap-2">
          {canSearch && (
            <div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
              <SearchIcon
                sx={{
                  fontSize: 16,
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--od-muted)",
                  pointerEvents: "none",
                }}
              />
              {/* Phone floor matches CollectionSearch: 44px and 16px text.
                  h-8 / text-xs on a 390px Overview table made iOS zoom the
                  page on focus (measured 32×196, font 12px, live main.1dd5e976.js). */}
              <input
                ref={inputRef}
                type="search"
                value={draft}
                placeholder={
                  searchPlaceholder ??
                  `Search ${searchableCount} ${
                    searchableCount === 1 ? "column" : "columns"
                  }…`
                }
                aria-label="Search records"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSearchChange?.(draft);
                  if (e.key === "Escape") {
                    setDraft("");
                    onSearchChange?.("");
                  }
                }}
                className="h-11 w-full rounded-md border border-od-interactive-boundary bg-od-surface pl-7 pr-11 text-base sm:h-8 sm:pr-8 sm:text-xs [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:pr-11 [&::-webkit-search-cancel-button]:appearance-none text-od-text outline-none transition-colors placeholder:text-od-muted focus:border-od-primary"
              />
              {draft && (
                <Button
                  aria-label="Clear search"
                  onClick={() => {
                    setDraft("");
                    onSearchChange?.("");
                    inputRef.current?.focus();
                  }}
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-1/2 -translate-y-1/2"
                >
                  <CloseIcon sx={{ fontSize: 13 }} />
                </Button>
              )}
            </div>
          )}

          {desktopTools ? (
            <div className="ml-auto flex max-w-full flex-wrap items-center gap-1.5">
              {hasExpandable && (
                <div
                  role="group"
                  aria-label="Related records"
                  className="flex flex-wrap items-center gap-1.5"
                >
                  <ExpansionTreeView
                    expandableFields={expandableFields}
                    expandedPaths={expandedPaths}
                    onToggle={onToggleExpansion}
                  />
                  <Tooltip title="Expand every related record">
                    <span>
                      <Button
                        variant="secondary" size="sm"
                        aria-label="Expand all related records"
                        onClick={onExpandAll}
                        disabled={expandedPaths.length === expandableFields.size}
                      >
                        <UnfoldMoreIcon sx={{ fontSize: 15 }} />
                        <span className="whitespace-nowrap">Expand all</span>
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Collapse every related record">
                    <span>
                      <Button
                        variant="secondary" size="sm"
                        aria-label="Collapse all related records"
                        onClick={onCollapseAll}
                        disabled={expandedPaths.length === 0}
                      >
                        <UnfoldLessIcon sx={{ fontSize: 15 }} />
                        <span className="whitespace-nowrap">Collapse</span>
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              )}

              <ColumnVisibilityManager
                columns={columns}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={onColumnVisibilityChange}
              />

              {onDensityChange && (
                <>
                  <Tooltip title="Row height">
                    <Button
                      variant="secondary" size="sm"
                      aria-label="Row height"
                      onClick={(e) => setDensityAnchor(e.currentTarget)}
                    >
                      <DensityMediumIcon sx={{ fontSize: 15 }} />
                    </Button>
                  </Tooltip>
                  <Menu
                    anchorEl={densityAnchor}
                    open={!!densityAnchor}
                    onClose={() => setDensityAnchor(null)}
                    slotProps={{ paper: { sx: menuPaperSx } }}
                    MenuListProps={{ dense: true, sx: { py: 0 } }}
                  >
                    {(["compact", "standard", "comfortable"] as const).map(
                      (d) => (
                        <MenuItem
                          key={d}
                          dense
                          selected={d === density}
                          onClick={() => {
                            onDensityChange(d);
                            setDensityAnchor(null);
                          }}
                          sx={{ ...menuItemSx, textTransform: "capitalize" }}
                        >
                          {d}
                        </MenuItem>
                      )
                    )}
                  </Menu>
                </>
              )}

              {onImport && (
                <Tooltip title={importLabel ?? "Import records from CSV"}>
                  <Button
                    variant="secondary" size="sm"
                    aria-label={importLabel ?? "Import records from CSV"}
                    onClick={() => importInputRef.current?.click()}
                  >
                    <FileUploadOutlinedIcon sx={{ fontSize: 15 }} />
                  </Button>
                </Tooltip>
              )}

              {onExport && (
                <Tooltip
                  title={exportLabel ?? "Export all matching records as CSV"}
                >
                  <Button
                    variant="secondary" size="sm"
                    aria-label={
                      exportLabel ?? "Export all matching records as CSV"
                    }
                    onClick={onExport}
                  >
                    <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />
                  </Button>
                </Tooltip>
              )}

              {onRefresh && (
                <Tooltip title="Reload rows">
                  <Button
                    variant="secondary" size="sm"
                    aria-label="Reload rows"
                    onClick={onRefresh}
                  >
                    <RefreshIcon
                      sx={{ fontSize: 15 }}
                      className={refreshing ? "animate-spin" : undefined}
                    />
                  </Button>
                </Tooltip>
              )}
            </div>
          ) : (
            <>
              <Tooltip title="More table tools">
                <Button
                  variant="secondary" size="icon" className="ml-auto"
                  aria-label="More table tools"
                  aria-haspopup="menu"
                  aria-expanded={!!overflowAnchor}
                  onClick={(e) => {
                    setMobileMenu("main");
                    setOverflowAnchor(e.currentTarget);
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 17 }} />
                </Button>
              </Tooltip>
              <Menu
                anchorEl={overflowAnchor}
                open={!!overflowAnchor}
                onClose={closeOverflow}
                slotProps={{ paper: { sx: menuPaperSx } }}
                MenuListProps={{ dense: true, sx: { py: 0 } }}
              >
                {mobileMenu === "main" && (
                  <>
                    <MenuItem
                      dense
                      aria-label="Show or hide columns"
                      onClick={() => setMobileMenu("columns")}
                      sx={menuItemSx}
                    >
                      <ViewColumnIcon sx={{ mr: 1, fontSize: 15 }} />
                      Columns
                    </MenuItem>
                    {onDensityChange && (
                      <MenuItem
                        dense
                        aria-label="Row height"
                        onClick={() => setMobileMenu("density")}
                        sx={{ ...menuItemSx, textTransform: "capitalize" }}
                      >
                        <DensityMediumIcon sx={{ mr: 1, fontSize: 15 }} />
                        Row height: {density}
                      </MenuItem>
                    )}
                    {onImport && (
                      <MenuItem
                        dense
                        aria-label={importLabel ?? "Import records from CSV"}
                        onClick={() => {
                          importInputRef.current?.click();
                          closeOverflow();
                        }}
                        sx={menuItemSx}
                      >
                        <FileUploadOutlinedIcon sx={{ mr: 1, fontSize: 15 }} />
                        Import CSV
                      </MenuItem>
                    )}
                    {onExport && (
                      <MenuItem
                        dense
                        aria-label={
                          exportLabel ?? "Export all matching records as CSV"
                        }
                        onClick={() => {
                          onExport();
                          closeOverflow();
                        }}
                        sx={menuItemSx}
                      >
                        <FileDownloadOutlinedIcon
                          sx={{ mr: 1, fontSize: 15 }}
                        />
                        Export CSV
                      </MenuItem>
                    )}
                    {onRefresh && (
                      <MenuItem
                        dense
                        aria-label="Reload rows"
                        onClick={() => {
                          onRefresh();
                          closeOverflow();
                        }}
                        sx={menuItemSx}
                      >
                        <RefreshIcon
                          sx={{ mr: 1, fontSize: 15 }}
                          className={refreshing ? "animate-spin" : undefined}
                        />
                        Reload rows
                      </MenuItem>
                    )}
                  </>
                )}
                {mobileMenu !== "main" && (
                  <MenuItem
                    dense
                    onClick={() => setMobileMenu("main")}
                    sx={menuItemSx}
                  >
                    Back
                  </MenuItem>
                )}
                {mobileMenu === "columns" && (
                  <>
                    <MenuItem
                      dense
                      onClick={() => {
                        const nextVisibility: { [key: string]: boolean } = {};
                        columns.forEach((column) => {
                          nextVisibility[column.field] = true;
                        });
                        onColumnVisibilityChange(nextVisibility);
                      }}
                      sx={menuItemSx}
                    >
                      Show all
                    </MenuItem>
                    <MenuItem
                      dense
                      onClick={() => {
                        const nextVisibility: { [key: string]: boolean } = {};
                        columns.forEach((column) => {
                          nextVisibility[column.field] =
                            column.field === "actions";
                        });
                        onColumnVisibilityChange(nextVisibility);
                      }}
                      sx={menuItemSx}
                    >
                      Hide all
                    </MenuItem>
                  </>
                )}
                {mobileMenu === "columns" &&
                  columns
                    .filter((column) => column.field !== "actions")
                    .map((column) => (
                      <MenuItem
                        key={column.field}
                        dense
                        selected={columnVisibility[column.field] !== false}
                        onClick={() =>
                          onColumnVisibilityChange({
                            ...columnVisibility,
                            [column.field]:
                              columnVisibility[column.field] === false,
                          })
                        }
                        sx={menuItemSx}
                      >
                        {column.headerName ?? column.field}
                      </MenuItem>
                    ))}
                {mobileMenu === "density" &&
                  (["compact", "standard", "comfortable"] as const).map(
                    (d) => (
                      <MenuItem
                        key={d}
                        dense
                        selected={d === density}
                        onClick={() => {
                          onDensityChange?.(d);
                          closeOverflow();
                        }}
                        sx={{ ...menuItemSx, textTransform: "capitalize" }}
                      >
                        {d}
                      </MenuItem>
                    )
                  )}
              </Menu>
            </>
          )}
        </div>

        {!desktopTools && hasExpandable && (
          <div
            role="group"
            aria-label="Related records"
            className="flex flex-wrap items-center gap-1.5"
          >
            <ExpansionTreeView
              expandableFields={expandableFields}
              expandedPaths={expandedPaths}
              onToggle={onToggleExpansion}
            />
            <Tooltip title="Expand every related record">
              <span>
                <Button
                  variant="secondary" size="sm"
                  aria-label="Expand all related records"
                  onClick={onExpandAll}
                  disabled={expandedPaths.length === expandableFields.size}
                >
                  <UnfoldMoreIcon sx={{ fontSize: 15 }} />
                  <span className="whitespace-nowrap">Expand all</span>
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Collapse every related record">
              <span>
                <Button
                  variant="secondary" size="sm"
                  aria-label="Collapse all related records"
                  onClick={onCollapseAll}
                  disabled={expandedPaths.length === 0}
                >
                  <UnfoldLessIcon sx={{ fontSize: 15 }} />
                  <span className="whitespace-nowrap">Collapse</span>
                </Button>
              </span>
            </Tooltip>
          </div>
        )}

        {filters}
      </div>
    );
  }
);
